import {
  getColor,
  getLabelsColors,
  LABEL_BASELINE_COLOR
} from 'common/constants/_colors';
import { GRAPH_TYPES, GROUP_BY_KEY } from '../../../consts';
import { calculatePercentage, isMulti } from 'common/utils/utils';
import map from 'lodash/map';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import { METRICS_GROUP_BY_OPTIONS } from '../../../custom-report.serivce';
import { getMetricsGroupByV3 } from '../../../custom-metrics-api.facade';
import { METRIC_TYPE } from '../../consts/graphTypes';
import { isMedianMetric, isFilteredByLabel } from '../../../custom-report.util';
import _ from 'lodash';

const groupEntityKeys = {
  [METRICS_GROUP_BY_OPTIONS.TEAM]: {
    color: 'color',
    name: 'name'
  }
};

const getMetricPointValue =
  ({ metricNameWithV3, percentOff }) =>
  metric => {
    const denominator = 1;
    const dataPoint = metric[metricNameWithV3] / denominator;
    if (percentOff && _.get(percentOff, 'metrics[0].name')) {
      const percentOffMetricName = percentOff.metrics[0].name;
      const percentVal = metric[percentOffMetricName] ?? 0;
      return {
        y: calculatePercentage(dataPoint || 0, percentVal, false),
        percentage: percentVal || 0,
        rawData: metric[metricNameWithV3]
      };
    }
    return dataPoint || 0;
  };

const mapSelectedEntities = filters => ({
  [METRICS_GROUP_BY_OPTIONS.TEAM]: keyBy(filters.globallySelectedTeams, 'id')
});

const getGroupEntity = (groupBy, groupId, filters) =>
  get(mapSelectedEntities(filters), `[${groupBy}][${groupId}]`);

const extractGroupEntityProps = (groupEntity, metricsGroupByKey) => {
  const keys = groupEntityKeys[metricsGroupByKey];

  const color = get(groupEntity, get(keys, 'color'));
  const name = get(groupEntity, get(keys, 'name'));

  return {
    name,
    color
  };
};

export const getYData = ({
  metrics = [],
  metricName,
  percentOff = null,
  metricsCalculationStrategy
}) => {
  const metricNameWithV3 = isMedianMetric(metricName)
    ? `${metricName}:${metricsCalculationStrategy}`
    : metricName;
  const metricsData = metrics?.[0]?.data ?? metrics;

  return metricsData.map(
    getMetricPointValue({
      metricNameWithV3,
      percentOff
    })
  );
};

const labelColors = (isBaseline, index) => {
  if (isBaseline) {
    return getColor(LABEL_BASELINE_COLOR);
  }

  return getColor(getLabelsColors[index]);
};

export const getSeries = ({
  widgetMetrics,
  metricName,
  percentOff,
  metricsCalculationStrategy,
  graph,
  filters
}) => {
  const isLabelSeries = isFilteredByLabel({ filters, type: graph.metricType });

  const totalFormatted = groupId => {
    const grouped = _.groupBy(widgetMetrics, GROUP_BY_KEY.GROUP) || {};
    const { total } = grouped[groupId]?.[0] || {};
    const average = total?.[metricName] || 0;
    return average;
  };

  const v3GroupBy = getMetricsGroupByV3(filters);

  const groupMetricsByFilter = () =>
    widgetMetrics
      .filter(
        metric =>
          !(graph.metricType === METRIC_TYPE.pm) || metric.group === graph.id
      )
      .reduce((acc, curr) => {
        acc[curr[GROUP_BY_KEY.GROUP] || GROUP_BY_KEY.GROUP] = curr.data;
        return acc;
      }, {});

  const groupMetrics = groupMetricsByFilter();

  const isMultiSeries = isMulti(groupMetrics);

  let labelCount = -1;
  return map(groupMetrics, (values, groupId) => {
    labelCount++;
    const seriesTotal = totalFormatted(groupId);
    const groupEntity = getGroupEntity(v3GroupBy.key, groupId, filters);
    const { name, color } = extractGroupEntityProps(groupEntity, v3GroupBy.key);
    const seriesName = isMultiSeries ? name : `organization`;
    const seriesColor = isMultiSeries
      ? color
      : getColor(graph.color ?? 'dodgerBlue');
    const isBaseline = isLabelSeries && groupId === 'baseline';

    return {
      name: isLabelSeries ? groupId : seriesName,
      total: isMultiSeries ? seriesTotal : null,
      data: getYData({
        metrics: values,
        metricName,
        percentOff,
        metricsCalculationStrategy
      }),
      color: isLabelSeries ? labelColors(isBaseline, labelCount) : seriesColor,
      type: isMultiSeries ? GRAPH_TYPES.SPLINE : GRAPH_TYPES.AREA_SPLINE,
      showInLegend: isMultiSeries,
      marker: {
        enabled: !isMultiSeries,
        symbol: 'circle',
        lineColor: null
      },
      customState: {
        groupId,
        metricsGroupByKey: v3GroupBy.key
      },
      isPreventLineClick: isBaseline,
      isLineHidden: isBaseline && Object.keys(groupMetrics).length > 2
    };
  });
};
